import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { showBanner } from '../../global-state/banner-slice'
import { newsletterServices, NewsletterSendFormData } from '../../services/newsletter-service'
import { NewsletterSendFormPresenter, FROM_EMAILS } from './newsletter-send-form-presenter'
import { GetNewsletterGroupsSearchParams } from '../../lib/types'
import { convertFileToBase64 } from './utils'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MAX_ATTACHMENT_SIZE_MB = 10
const DEFAULT_LIMIT = 10
const DEFAULT_SEARCH_PARAMS: GetNewsletterGroupsSearchParams = {
  limit: DEFAULT_LIMIT.toString(),
  offset: '0',
  keyword: '',
  sortBy: 'NEWEST_TO_OLDEST',
}
const DEFAULT_PAGINATION = {
  total: 0,
  limit: DEFAULT_LIMIT,
  count: 0,
  offset: 0,
}

export type NewsletterGroup = {
  id: string
  name: string
  description: string
  emailCount: number
}

export type AttachmentFile = {
  name: string
  size: number
  base64Data: string
}

const defaultValues: NewsletterSendFormData = {
  includeSubscribers: false,
  groupIds: [],
  subject: '',
  body: '',
  replyTo: '',
  fromEmail: '',
  cc: [],
  bcc: [],
  headerTemplate: 'NONE',
  footerTemplate: 'NONE',
  attachments: [],
}

function NewsletterSendFormContainer() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const form = useForm<NewsletterSendFormData>({
    defaultValues,
    mode: 'onBlur',
  })

  const [groups, setGroups] = useState<NewsletterGroup[]>([])
  const [isLoadingGroups, setIsLoadingGroups] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [attachments, setAttachments] = useState<AttachmentFile[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [searchParams, setSearchParams] =
    useState<GetNewsletterGroupsSearchParams>(DEFAULT_SEARCH_PARAMS)
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)

  useEffect(() => {
    const fetchGroups = async () => {
      setIsLoadingGroups(true)
      try {
        if (parseInt(searchParams.offset) !== 0) {
          const res = await newsletterServices.getGroups(searchParams)
          if (res?.data?.groups && res.data.groups.length > 0) {
            setGroups((prev) => [
              ...prev,
              ...res.data.groups.map((group: any) => ({
                id: group.id,
                name: group.name,
                description: group.description,
                emailCount: group.emails?.length ?? 0,
              })),
            ])
            if (res.data.pagination) {
              setPagination(res.data.pagination)
            }
          }
        } else {
          const res = await newsletterServices.getGroups(searchParams)
          if (res?.data?.groups && res.data.groups.length > 0) {
            setGroups(
              res.data.groups.map((group: any) => ({
                id: group.id,
                name: group.name,
                description: group.description,
                emailCount: group.emails?.length ?? 0,
              })),
            )
            if (res.data.pagination) {
              setPagination(res.data.pagination)
            }
          } else {
            setGroups([])
            setPagination(DEFAULT_PAGINATION)
          }
        }
      } catch {
        dispatch(showBanner({ text: 'Failed to load newsletter groups', variant: 'error-banner' }))
      } finally {
        setIsLoadingGroups(false)
      }
    }
    fetchGroups()
  }, [searchParams])

  const handleAttachmentUpload = useCallback(
    async (file: File) => {
      const sizeInKB = Math.round(file.size / 1024)
      if (sizeInKB > MAX_ATTACHMENT_SIZE_MB * 1024) {
        dispatch(
          showBanner({
            text: `File size must be under ${MAX_ATTACHMENT_SIZE_MB} MB`,
            variant: 'error-banner',
          }),
        )
        return
      }
      setIsUploading(true)
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const base64Data = await convertFileToBase64(file)

        const newAttachment: AttachmentFile = {
          name: file.name,
          size: file.size,
          base64Data,
        }
        setAttachments((prev) => {
          const next = [...prev, newAttachment]
          form.setValue('attachments', next, { shouldDirty: true })
          return next
        })
      } catch {
        dispatch(showBanner({ text: 'Failed to upload attachment', variant: 'error-banner' }))
      } finally {
        setIsUploading(false)
      }
    },
    [dispatch, form],
  )

  const handleRemoveAttachment = useCallback(
    (name: string) => {
      setAttachments((prev) => {
        const next = prev.filter((a) => a.name !== name)
        form.setValue('attachments', next, { shouldDirty: true })
        return next
      })
    },
    [form],
  )

  const validateEmails = (emails: string[]) => emails.every((e) => EMAIL_REGEX.test(e.trim()))

  const onSubmit = async (data: NewsletterSendFormData) => {
    if (data.groupIds.length === 0 && data.includeSubscribers === false) {
      form.setError('includeSubscribers', {
        type: 'manual',
        message: 'Select at least one group or include all newsletter subscribers',
      })
      return
    }
    if (!data.subject) {
      form.setError('subject', { type: 'manual', message: 'Subject is required' })
      return
    }
    if (!data.fromEmail) {
      form.setError('fromEmail', { type: 'manual', message: 'From email is required' })
      return
    }
    if (!FROM_EMAILS.some((email) => email.value === data.fromEmail)) {
      form.setError('fromEmail', { type: 'manual', message: 'Invalid from email' })
      return
    }
    if (data.cc.length > 0 && !validateEmails(data.cc)) {
      form.setError('cc', { type: 'manual', message: 'One or more CC emails are invalid' })
      return
    }
    if (data.bcc.length > 0 && !validateEmails(data.bcc)) {
      form.setError('bcc', { type: 'manual', message: 'One or more BCC emails are invalid' })
      return
    }
    if (data.replyTo && !EMAIL_REGEX.test(data.replyTo.trim())) {
      form.setError('replyTo', { type: 'manual', message: 'Enter a valid reply-to email' })
      return
    }

    setIsSubmitting(true)
    try {
      const payload: NewsletterSendFormData = {
        ...data,
        cc: data.cc.filter(Boolean),
        bcc: data.bcc.filter(Boolean),
      }
      const res = await newsletterServices.sendNewsletter(payload)
      if (res?.status === 200 || res?.status === 201) {
        dispatch(showBanner({ text: 'Newsletter sent successfully', variant: 'success-banner' }))
      } else {
        dispatch(
          showBanner({
            text: res?.data?.message || 'Failed to send newsletter. Please try again.',
            variant: 'error-banner',
          }),
        )
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      dispatch(
        showBanner({
          text:
            error?.response?.data?.message ||
            error?.message ||
            'Failed to send newsletter. Please try again.',
          variant: 'error-banner',
        }),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleLoadMore = () => {
    setSearchParams((prev) => ({
      ...prev,
      offset: (pagination.count + pagination.offset).toString(),
    }))
  }

  return (
    <NewsletterSendFormPresenter
      form={form}
      groups={groups}
      isLoadingGroups={isLoadingGroups}
      isSubmitting={isSubmitting}
      isUploading={isUploading}
      attachments={attachments}
      showPreview={showPreview}
      onSubmit={onSubmit}
      onAttachmentUpload={handleAttachmentUpload}
      onRemoveAttachment={handleRemoveAttachment}
      onTogglePreview={() => setShowPreview((p) => !p)}
      pagination={pagination}
      handleLoadMore={handleLoadMore}
    />
  )
}

export { NewsletterSendFormContainer }
